import type { Component, ComponentOverride } from '@wix/thunderbolt-becky-types'
import type { CssObject, Modes, SelectorObj } from '../shared.types'
import type { CompNode, NodeRefs } from '@wix/thunderbolt-catharsis'
import { createCompNode } from '@wix/thunderbolt-catharsis'
import { mapValues } from '../utils/mapValues'
import _ from 'lodash'
import { DEFAULT_DATA_MODE, HOVER_DATA_MODE } from '@wix/thunderbolt-commons'
import { areCssAttributesEqual, getDifferingAttributes, mergeSelectors } from '../utils/selectorsUtils'

export type StructureOverrides = Array<keyof ComponentOverride>
type ComponentModes = { [modeId: string]: Component }

const DEFAULT_SELECTOR = ''
const SIZE = ['height', 'width']

const EMPTY_ARRAY: Array<ComponentOverride> = []

const getComponentsModes = (component: Component, overrides: StructureOverrides): ComponentModes =>
	(component.modes?.overrides || EMPTY_ARRAY).reduce<ComponentModes>(
		(acc, override) => {
			if (overrides.some((field) => field in override) && override.modeIds) {
				const [modeId] = override.modeIds
				acc[modeId] = { ...component, ...override }
			}
			return acc
		},
		{
			'': component.modes?.isHiddenByModes
				? ({ ...component, isHiddenByModes: component.modes?.isHiddenByModes } as Component)
				: component,
		}
	)

export const withModes = <TDependencies, TViewerItem, TComponentNodeRefs>(
	compNode: CompNode<TDependencies, TViewerItem, TComponentNodeRefs>,
	structureOverrides: StructureOverrides,
	mergeCSSModes = (css: SelectorObj) => css
) =>
	createCompNode({
		getDependencies: (component: Component, refs: NodeRefs<TComponentNodeRefs>) => {
			const modes = getComponentsModes(component, structureOverrides)

			return {
				modes: mapValues(modes, (__, modeId) => (modeId ? refs.modes(modeId) : null)),
				deps: mapValues(modes, (comp) => compNode.getDependencies(comp, refs)),
			}
		},
		toViewItem: (component, deps) => {
			const modes = getComponentsModes(component, structureOverrides)
			const css = Object.keys(modes).reduce<Partial<Record<Modes, TViewerItem>>>((acc, modeId) => {
				const prefix = deps.modes[modeId]?.prefix || ''
				const viewerItem = compNode.toViewItem(modes[modeId], deps.deps[modeId]) as TViewerItem
				if (viewerItem) {
					acc[prefix] = viewerItem
				}
				return acc
			}, {})
			return { type: 'classic' as const, css: css[DEFAULT_SELECTOR] ? mergeCSSModes(css as SelectorObj) : {} }
		},
	})

export const withMapperModes = (
	variantsCompNode: CompNode<any, any, any>,
	modesCompNode: CompNode<any, any, any>,
	structureOverrides: (component: Component) => StructureOverrides
) =>
	createCompNode<{ modes: any; deps: any }, any, any>({
		getDependencies: (component: Component, refs) => {
			if (!component.modes) {
				return variantsCompNode.getDependencies(component, refs)
			}

			const modes = getComponentsModes(component, structureOverrides(component))

			return {
				modes: mapValues(modes, (__, modeId) => (modeId ? refs.modes(modeId) : null)),
				deps: mapValues(modes, (comp) => modesCompNode.getDependencies(comp, refs)),
			}
		},
		toViewItem: (component, deps: { modes: any; deps: any }) => {
			if (!component.modes) {
				return variantsCompNode.toViewItem(component, deps)
			}

			const modes = getComponentsModes(component, structureOverrides(component))
			const depsByMode = Object.keys(modes).reduce<Partial<Record<Modes, any>>>((acc, modeId) => {
				const prefix = deps.modes[modeId]?.prefix || ''
				const viewerItem = modesCompNode.toViewItem(modes[modeId], deps.deps?.[modeId])
				if (viewerItem) {
					// @ts-ignore
					acc[prefix] = viewerItem
				}
				return acc
			}, {})
			return { type: 'classic' as const, css: mergeModesForMappersCss(depsByMode as SelectorObj) }
		},
	})

const mergeModesForMappersCss = (css: SelectorObj) => {
	if (!css) {
		return null
	}
	const { [DEFAULT_DATA_MODE]: defaultOverride, [DEFAULT_SELECTOR]: initialDefaultSelector, ...hover } = css

	return {
		'': defaultOverride || initialDefaultSelector,
		...hover,
	}
}

//* HOVER BOX LOGIC *
// size (height and width) should be translated to css only in the default selector.
// UNLESS- both hover and default are visible, in which case we need to write the size to both selectors.
// we need to determined what size should be written there.
// the determination is done by inspecting the visibility of each mode, when the visible mode/ override wins.
// if default selector !== default override and hover override doesn't exist, we need to create hover override and make it the same as default mode.
// default and hover overrides should get all their missing attributes from the default selector.
// we need to make default selector to be default override and delete default override.
// data-mode="default" should be displaying only the visibility attribute and the default selector should have no visibility attribute.

export const mergeCSSModes = (css: SelectorObj) => {
	const { [DEFAULT_DATA_MODE]: defaultOverride, [DEFAULT_SELECTOR]: initialDefaultSelector } = css
	let { [HOVER_DATA_MODE]: hoverOverride } = css
	const hoverModeExistsInStructure = hoverOverride !== undefined

	if (defaultOverride && !areCssAttributesEqual(defaultOverride, initialDefaultSelector)) {
		hoverOverride = mergeSelectors(initialDefaultSelector, hoverOverride)
	}
	const defaultVisibility = { visibility: defaultOverride?.visibility || initialDefaultSelector.visibility }
	const defaultOverrideWithoutVisibility = _.omit(defaultOverride, ['visibility'])

	css[DEFAULT_SELECTOR] = mergeSelectors(initialDefaultSelector, defaultOverrideWithoutVisibility, defaultVisibility)
	const defaultCss = css[DEFAULT_SELECTOR]
	const defaultCssWithoutSize = _.omit(defaultCss, SIZE)

	if (hoverOverride) {
		handleHoverMode(css, defaultCss, hoverOverride, hoverModeExistsInStructure, defaultCssWithoutSize)
	} else if (defaultOverride && !areCssAttributesEqual(defaultOverride, defaultCss)) {
		css[HOVER_DATA_MODE] = defaultCssWithoutSize
	}

	css[DEFAULT_DATA_MODE] = defaultVisibility

	removeVisibilityAttributes(css)

	return css
}

const handleHoverMode = (
	css: SelectorObj,
	defaultCss: CssObject,
	hoverOverride: CssObject,
	hoverModeExistsInStructure: boolean,
	defaultCssWithoutSize: CssObject
) => {
	if (defaultCss.visibility === 'hidden' && hoverModeExistsInStructure) {
		css[DEFAULT_SELECTOR] = mergeSelectors(defaultCss, _.omit(hoverOverride, ['visibility']))
	}
	css[HOVER_DATA_MODE] = determineHoverOverride(
		css,
		hoverOverride,
		defaultCssWithoutSize,
		_.omit(hoverOverride, SIZE)
	)

	const doesHoverModeAndDefaultSelectorContainOnlyVisibility = _.isEmpty({
		..._.omit(css[HOVER_DATA_MODE], ['visibility']),
		..._.omit(css[DEFAULT_SELECTOR], ['visibility']),
	})

	if (
		!doesHoverModeAndDefaultSelectorContainOnlyVisibility &&
		_.isEmpty(getDifferingAttributes(hoverOverride, defaultCss))
	) {
		delete css[HOVER_DATA_MODE]
	}
}

const determineHoverOverride = (
	css: SelectorObj,
	hoverOverride: CssObject,
	defaultCssWithoutSize: CssObject,
	hoverOverrideWithoutSize: CssObject
): CssObject => {
	if (hoverOverride.visibility === 'hidden') {
		return hoverOverrideWithoutSize
	}

	return areCssAttributesEqual(defaultCssWithoutSize, hoverOverrideWithoutSize)
		? _.pick(hoverOverride, SIZE)
		: getDifferingAttributes(hoverOverride, css[DEFAULT_SELECTOR])
}

const removeVisibilityAttributes = (css: SelectorObj) => {
	if (
		css[HOVER_DATA_MODE] &&
		css[DEFAULT_DATA_MODE] &&
		css[HOVER_DATA_MODE].visibility === css[DEFAULT_DATA_MODE].visibility
	) {
		css[DEFAULT_SELECTOR].visibility = css[HOVER_DATA_MODE].visibility
		delete css[HOVER_DATA_MODE].visibility
		delete css[DEFAULT_DATA_MODE].visibility
	} else {
		delete css[DEFAULT_SELECTOR].visibility
	}

	for (const selector in css) {
		if (css[selector].visibility === 'visible') {
			delete css[selector].visibility
		}
		if (selector !== DEFAULT_SELECTOR && _.isEmpty(css[selector])) {
			delete css[selector]
		}
	}
}
